import React from 'react';
import { styled } from '@compiled/react';
import { SOFTWARE_VERSION_DETAILS_ISSUE } from '@atlassian/jira-common-constants/src/analytics-sources.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useRefetchContext } from '@atlassian/jira-software-releases-version-detail-refetch-util/src/index.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const IssueApp = lazy(
	() =>
		import(
			/* webpackChunkName: "async-issue-app" */ '@atlassian/jira-issue-view/src/views/issue-details/issue-app'
		),
);

type Props = {
	issueKey: IssueKey;
	onClose: () => void;
};

export const Issue = ({ issueKey, onClose }: Props): React.JSX.Element => {
	const { refetch } = useRefetchContext();

	return (
		<ContextualAnalyticsData sourceType={SCREEN} sourceName="versionDetailsIssue">
			<DetailsWrapper>
				<Placeholder name="issue-app" fallback={null}>
					<IssueApp
						viewModeOptions={{ viewMode: 'SIDEBAR' }}
						issueKey={issueKey}
						shouldSetInitialFocus
						shouldShowCloseButton
						// TODO: Once all fields used in the issue link component and the version
						// details issue list have been migrated to use Relay, we should be able to
						// remove the onChange callback used here, since the data will sync to the
						// Relay store without having to call "refetch" here
						onChange={(event) => {
							if (event.type === 'FIELD_CHANGED') {
								refetch();
							}
						}}
						onIssueDeleteSuccess={refetch}
						onClose={onClose}
						isLoadedWithPage={false}
						isSpaEnabled={false}
						analyticsSource={SOFTWARE_VERSION_DETAILS_ISSUE}
					/>
				</Placeholder>
			</DetailsWrapper>
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DetailsWrapper = styled.div({
	width: '100%',
	height: '100%',
	overflowX: 'hidden',
});
